import { useMemo } from 'react';
import { useSessionStore } from 'store/session';
import { MeterModelsDB } from 'indexed_db/meterModelsDB';
import { ReplaceMeterDb } from 'indexed_db/replaceMeterDb';
import { customApiKeyAuthenticator } from './httpClient';
import { InventoryMeterInstaller } from './inventoryMeterInstaller';
import { InventoryMetersApiClient } from './api/inventoryMeters';
import { InventoryMeterReadingGroupsApiClient } from './api/inventoryMeterReadingGroups';
import { MeterModelsApiClient } from './api/meterModels';
import { InventoryLocationBuildingsApiClient } from './api/inventoryLocationBuildings';
import { EnmonToolsApiClient } from './api/enmonTools';
import { ThirdPartiesApiClient } from './api/thirdParties';
import { InventoryLocationsApiClient } from './api/inventoryLocations';

export interface Services {
  api: {
    inventoryLocationsApiClient: InventoryLocationsApiClient;
    inventoryLocationBuildingsApiClient: InventoryLocationBuildingsApiClient;
    inventoryMetersApiClient: InventoryMetersApiClient;
    inventoryMeterReadingGroupsApiClient: InventoryMeterReadingGroupsApiClient;
    meterModelsApiClient: MeterModelsApiClient;
    thirdPartiesApiClient: ThirdPartiesApiClient;
  };
  enmonTools: {
    meters: {
      find: EnmonToolsApiClient['getMeters'];
      findOne: EnmonToolsApiClient['getMeter'];
    };
    decoder: {
      getMessages: EnmonToolsApiClient['getDecodedMessages'];
    };
    files: {
      getFileLink: EnmonToolsApiClient['getFileLink'];
      getFiles: EnmonToolsApiClient['getFiles'];
    };
  };
  indexedDb: {
    meterModelsDb: MeterModelsDB;
    replaceMeterDb: ReplaceMeterDb;
  };
  inventoryMeterInstaller: InventoryMeterInstaller;
}

export const useServices = (): Services => {
  const { apiKey } = useSessionStore();

  return useMemo(() => {
    const authenticator = apiKey ? customApiKeyAuthenticator(apiKey) : undefined;

    // Api
    const inventoryLocationsApiClient = new InventoryLocationsApiClient();
    const inventoryLocationBuildingsApiClient = new InventoryLocationBuildingsApiClient(authenticator);
    const inventoryMetersApiClient = new InventoryMetersApiClient(authenticator);
    const inventoryMeterReadingGroupsApiClient = new InventoryMeterReadingGroupsApiClient(authenticator);
    const meterModelsApiClient = new MeterModelsApiClient(authenticator);
    const enmonToolsApiClient = new EnmonToolsApiClient(authenticator);
    const thirdPartiesApiClient = new ThirdPartiesApiClient(authenticator);

    // Indexed DB
    const meterModelsDb = new MeterModelsDB();
    const replaceMeterDb = new ReplaceMeterDb();

    // Services
    const inventoryMeterInstaller = new InventoryMeterInstaller(inventoryMetersApiClient);

    return {
      api: {
        inventoryLocationsApiClient,
        inventoryLocationBuildingsApiClient,
        inventoryMetersApiClient,
        inventoryMeterReadingGroupsApiClient,
        meterModelsApiClient,
        thirdPartiesApiClient,
      },
      enmonTools: {
        meters: {
          find: enmonToolsApiClient.getMeters.bind(enmonToolsApiClient),
          findOne: enmonToolsApiClient.getMeter.bind(enmonToolsApiClient),
        },
        decoder: {
          getMessages: enmonToolsApiClient.getDecodedMessages.bind(enmonToolsApiClient),
        },
        files: {
          getFileLink: enmonToolsApiClient.getFileLink.bind(enmonToolsApiClient),
          getFiles: enmonToolsApiClient.getFiles.bind(enmonToolsApiClient),
        },
      },
      // Indexed DB
      indexedDb: {
        meterModelsDb,
        replaceMeterDb,
      },
      inventoryMeterInstaller,
    };
  }, [apiKey]);
};
